import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { Api } from '../../../api';
import { API_BASE_URL } from '../../../core/env.configs';
import { getJWTToken, errorToast } from '../../../utils/helper';
import {
  StatCard,
  Panel,
  MiniDonutCard,
  ProgressList,
  StandardButton,
  STAT_ICONS,
  toneColors
} from '../../../component/common/designSystem';

/**
 * The B2B business overview — company pipeline, acquisition channels and the
 * mood of company admins — on the design system.
 *
 * Replaces CompanyCount.js, which the Business Hub dashboard and the Earnings
 * page both rendered. Same four endpoints, same download; three things fixed
 * on the way:
 *
 *  - The channel and mood charts were SEEDED with invented numbers (100, 80,
 *    60, 20) and displayed them until the real response replaced them. On a
 *    slow connection, or a failed request, the page showed fake figures as if
 *    they were real. Nothing here renders before its data has arrived.
 *  - Active and inactive were a different pair of colours on every card
 *    (navy, pink, green, maroon, mustard). They are now the same two tones
 *    everywhere, which is what lets the cards be compared.
 *  - A failed download failed silently. It now says so.
 */

const SPLIT_COLORS = toneColors(['success', 'neutral']);

const PAIRS = [
  {
    key: 'companies',
    title: 'All companies',
    active: 'activeCompanies',
    inactive: 'inactiveCompanies',
    unit: 'companies'
  },
  {
    key: 'negotiation',
    title: 'In negotiation',
    active: 'negotiationActive',
    inactive: 'negotiationInactive',
    unit: 'companies'
  },
  {
    key: 'signed',
    title: 'Signed',
    active: 'signedActive',
    inactive: 'signedInactive',
    unit: 'companies'
  },
  {
    key: 'users',
    title: 'B2B users',
    active: 'activeCompaniesUsers',
    inactive: 'inactiveCompaniesUsers',
    unit: 'users'
  },
  {
    key: 'seats',
    title: 'Seats under contract',
    active: 'totalactiveSeats',
    inactive: 'totalinactiveSeats',
    unit: 'seats'
  }
];

/**
 * How companies found Shoorah. "Total" is deliberately not a row: it is the
 * sum of the others, and as a bar it would always be the longest and flatten
 * every real channel beneath it.
 */
const CHANNELS = [
  { key: 'totalViaWebsite', label: 'Website' },
  { key: 'totalViaSales', label: 'Sales team' },
  { key: 'totalViaPaidAds', label: 'Paid advertising' },
  { key: 'totalViaGoogle', label: 'Google' },
  { key: 'totalViaFacebook', label: 'Facebook' },
  { key: 'totalViaLinkedin', label: 'LinkedIn' }
];

/** Mood buckets, coloured by what they mean rather than by position. */
const MOODS = [
  { key: 'happy', label: 'Happy', tone: 'success' },
  { key: 'neutral', label: 'Neutral', tone: 'neutral' },
  { key: 'sad', label: 'Sad', tone: 'info' },
  { key: 'angry', label: 'Angry', tone: 'danger' }
];

const num = (value) => Number(value) || 0;

const B2BOverview = ({ title, showDownload = false, showInsights = false }) => {
  const [counts, setCounts] = useState(null);
  const [channels, setChannels] = useState(null);
  const [moods, setMoods] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const requests = [
      Api.getEarningCount().then((res) => {
        if (!cancelled && res?.data?.meta?.code === 1) setCounts(res.data.data || {});
      }),
      // The channel and mood panels only exist on the dashboard; the Earnings
      // page does not pay for two requests it would never show.
      showInsights
        ? Api.getB2BviaGraphData().then((res) => {
            if (!cancelled) setChannels(res?.data?.data || {});
          })
        : Promise.resolve(),
      showInsights
        ? Api.getB2bMoodData().then((res) => {
            if (!cancelled) setMoods(res?.data?.data || {});
          })
        : Promise.resolve()
    ];

    Promise.allSettled(requests).finally(() => {
      if (!cancelled) setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [showInsights]);

  const total = (pair) => num(counts?.[pair.active]) + num(counts?.[pair.inactive]);
  const figure = (value) => (loading && !counts ? '…' : value.toLocaleString());

  /**
   * The server renders the PDF from what this page already loaded, so the
   * report is exactly what the admin is looking at rather than a second,
   * possibly different, set of queries.
   */
  const download = useCallback(async () => {
    setDownloading(true);
    try {
      const res = await axios.post(
        `${API_BASE_URL}admin/v1/b2b-overall-report`,
        { companyData: counts, b2bData: channels, b2bMoodData: moods },
        {
          headers: {
            'Content-Type': 'application/json',
            deviceType: 3,
            Authorization: getJWTToken()
          },
          responseType: 'blob'
        }
      );
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = 'B2BOverallReport.pdf';
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      errorToast('The report could not be downloaded. Please try again.');
    } finally {
      setDownloading(false);
    }
  }, [counts, channels, moods]);

  return (
    <div className="flex flex-col gap-4">
      {title || showDownload ? (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          {title ? (
            <h2 className="text-lg font-medium text-shoorah-lightBlack dark:text-shoorah-darkBg">
              {title}
            </h2>
          ) : (
            <span />
          )}
          {showDownload ? (
            <StandardButton
              variant="secondary"
              onClick={download}
              disabled={downloading || !counts}
            >
              {downloading ? 'Preparing…' : 'Download report'}
            </StandardButton>
          ) : null}
        </div>
      ) : null}

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
        <StatCard icon={STAT_ICONS.company} title="Companies" value={figure(total(PAIRS[0]))} />
        <StatCard icon={STAT_ICONS.completions} title="Signed" value={figure(total(PAIRS[2]))} />
        <StatCard icon={STAT_ICONS.users} title="B2B users" value={figure(total(PAIRS[3]))} />
        <StatCard
          icon={STAT_ICONS.subscription}
          title="New contracts this month"
          value={figure(num(counts?.newContractsThisMonthCount))}
          note={
            counts ? `${num(counts.contractsLastMonthCount).toLocaleString()} last month` : null
          }
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3">
        {PAIRS.map((pair) => (
          <MiniDonutCard
            key={pair.key}
            title={pair.title}
            subtitle="Active and inactive"
            unit={pair.unit}
            colors={SPLIT_COLORS}
            legendLimit={2}
            data={[
              { label: 'Active', count: num(counts?.[pair.active]) },
              { label: 'Inactive', count: num(counts?.[pair.inactive]) }
            ]}
          />
        ))}
      </div>

      {showInsights ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Panel
            title="How companies found us"
            subtitle={
              channels
                ? `${num(channels.totalCompanies).toLocaleString()} companies in total`
                : null
            }
          >
            {channels ? (
              <ProgressList
                items={CHANNELS.map((channel) => ({
                  label: channel.label,
                  count: num(channels[channel.key])
                }))}
                unit="companies"
              />
            ) : null}
          </Panel>

          {moods ? (
            <MiniDonutCard
              title="Company admin mood"
              subtitle="How B2B admins are feeling"
              unit="check-ins"
              legendLimit={4}
              colors={toneColors(MOODS.map((mood) => mood.tone))}
              data={MOODS.map((mood) => ({ label: mood.label, count: num(moods[mood.key]) }))}
            />
          ) : (
            <Panel title="Company admin mood" />
          )}
        </div>
      ) : null}
    </div>
  );
};

B2BOverview.propTypes = {
  title: PropTypes.string,
  showDownload: PropTypes.bool,
  // The acquisition-channel and admin-mood panels. The Business Hub dashboard
  // shows them; the Earnings page, which only wants the pipeline, does not.
  showInsights: PropTypes.bool
};

export default B2BOverview;
